import PageHeader from "../../components/Header/PageHeader";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchWrapper } from "../../helpers";
import {
    Layout, Row, Col, Card, Space, Typography, Button, Badge, Divider,
    Select, Input, Segmented, Spin, Pagination
} from "antd";

import { PlanCard } from "../ContentPlan/PlanCard";
import { mergeStateObject } from "../../utils/merger";
import { useOrganization } from "../../context/organizationContext";
import { useSelector } from "react-redux";
import AppNavigation from "../../components/AppNavigation";
import {ImeanHeader} from "../../components/Header/Header";

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const ModerationPage = () => {
    const user = useSelector((obj) => obj.auth.user);
    const org = useOrganization();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [isPostsLoading, setIsPostsLoading] = useState(true);
    const [filter, setFilter] = useState("user_approved");
    const [pagination, setPagination] = useState({
        page: 1,
        size: +localStorage.getItem("paginationSize") || 10,
        count: 0,
    });
    const [pagesByFilter, setPagesByFilter] = useState({
        user_approved: 1,
        moderator_approved: 1,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [orgUsers, setOrgUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    // approvedExtended 'user_approved' 'moderator_approved'
    useEffect(() => {
        setIsPostsLoading(true);
        fetchWrapper
            .get(`${baseUrl}/post/moderation?page=${pagination.page}&limit=${pagination.size}&approvedExtended=${filter}&userId=${selectedUserId}&sortBy=${sortBy}&sortOrder=${sortOrder}`)
            .then((res) => {
                setPosts(res?.posts);
                mergeStateObject(setPagination, { count: res?.count || 0 });
                setIsPostsLoading(false);
            })
            .catch((err) => {
                setIsPostsLoading(false);
            });
    }, [pagination.page, pagination.size, filter, selectedUserId, sortBy, sortOrder]);

    // Debounced search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchQuery !== '') {
                mergeStateObject(setPagination, { page: 1 });
                setIsPostsLoading(true);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

// Immediate sorting changes
    useEffect(() => {
        mergeStateObject(setPagination, { page: 1 });
        setIsPostsLoading(true);
    }, [sortBy, sortOrder]);

    useEffect(() => {
        if (org?.organization?.employees) {
            setOrgUsers(org.organization.employees);
        }
    }, [org]);

    useEffect(() => {
        mergeStateObject(setPagination, { page: 1 });
        setIsPostsLoading(true);
    }, [selectedUserId, sortBy, sortOrder]);

    // 1) Пока org/user не загружены — ничего не редиректим
    const isOrgLoaded = !!org?.organization;
    const isUserLoaded = user !== undefined && user !== null

    useEffect(() => {
        if (!isOrgLoaded || !isUserLoaded) return;
        if (!(org.organization?.moderatorExist && user?.moderatorOrg)) {
            navigate("/", { replace: true });
        }
    }, [isOrgLoaded, isUserLoaded, org?.organization, user, navigate])

    if (!isOrgLoaded || !isUserLoaded) {
        return (
            <Layout style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Spin size="large" tip={t("functions.loading")} />
            </Layout>
        );
    }

    return (
        <Layout style={{ minHeight: "100vh", background: "linear-gradient(180deg, #ffffff, #faf5ff)" }}>
            {/* TOP BAR */}
            <ImeanHeader initialDark={true}></ImeanHeader>
            <Layout.Header
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 20,
                    background: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(6px)",
                    borderBottom: "1px solid #f0f0f0",
                    paddingInline: 16,
                }}
            >
                <Row align="middle" justify="space-between" style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
                    <Space align="center" size={12}>
                        <Button onClick={() => navigate("/home")}>{t("common.back")}</Button>
                        <Badge color="#7c3aed" />
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            {t("moderation.header")}
                        </Typography.Title>
                    </Space>
                    <Space>
                        <Button type="primary" onClick={() => navigate("/post-distribution")}>
                            {t("postDistribution.postDistributionTagName")}
                        </Button>
                    </Space>
                </Row>
            </Layout.Header>

            {/* CONTENT */}
            <Layout.Content style={{ maxWidth: 1200, margin: "24px auto", width: "100%", padding: "0 16px" }}>
                {/* Toolbar */}
                <Card className="shadow-sm" bodyStyle={{ padding: 16 }}>
                    <Row gutter={[12, 12]} align="middle" justify="space-between">
                        {/* Filters (left) */}
                        <Col xs={24} lg={12}>
                            <Space wrap>
                                <Select
                                    placeholder={t("functions.selectUser")}
                                    value={selectedUserId}
                                    onChange={(v) => setSelectedUserId(v)}
                                    style={{ minWidth: 220, maxWidth: 320 }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                    options={[
                                        { value: "", label: t("functions.allUsers") },
                                        ...orgUsers.map((u) => ({ value: u.id, label: u.fullName || u.email })),
                                    ]}
                                />
                                <Input.Search
                                    allowClear
                                    placeholder={t("functions.searchPosts")}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onSearch={() => {
                                        mergeStateObject(setPagination, { page: 1 });
                                        setIsPostsLoading(true);
                                    }}
                                    style={{ minWidth: 220, maxWidth: 360 }}
                                />
                            </Space>
                        </Col>

                        {/* Sort (right) */}
                        <Col xs={24} lg="auto">
                            <Space wrap split={<Divider type="vertical" />}>
                                <Space>
                                    <Typography.Text type="secondary">{t("functions.sortBy")}:</Typography.Text>
                                    <Select
                                        value={sortBy}
                                        onChange={(v) => setSortBy(v)}
                                        style={{ width: 160 }}
                                        options={[
                                            { value: "date", label: t("functions.date") },
                                            { value: "user", label: t("functions.user") },
                                        ]}
                                    />
                                </Space>
                                <Space>
                                    <Typography.Text type="secondary">{t("functions.order")}:</Typography.Text>
                                    <Select
                                        value={sortOrder}
                                        onChange={(v) => setSortOrder(v)}
                                        style={{ width: 160 }}
                                        options={[
                                            { value: "asc", label: t("functions.ascending") },
                                            { value: "desc", label: t("functions.descending") },
                                        ]}
                                    />
                                </Space>
                            </Space>
                        </Col>
                    </Row>

                    <Divider style={{ margin: "12px 0" }} />

                    {/* Moderation status switcher */}
                    <Row align="middle" justify="center">
                        <Segmented
                            value={filter}
                            onChange={(val) => {
                                if (val !== filter) {
                                    setIsPostsLoading(true);
                                    setFilter(val);
                                    mergeStateObject(setPagination, { page: pagesByFilter[val] ?? 1 });
                                }
                            }}
                            options={[
                                { label: t("moderation.onModeration"), value: "user_approved" },
                                { label: t("moderation.archive"), value: "moderator_approved" },
                            ]}
                            block
                        />
                    </Row>
                </Card>

                {/* List */}
                <div style={{ marginTop: 16 }}>
                    {isPostsLoading ? (
                        <Card className="shadow-sm" bodyStyle={{ padding: 32, textAlign: "center" }}>
                            <Space direction="vertical" align="center">
                                <Spin size="large" />
                                <Typography.Text type="secondary">{t("functions.loading")}</Typography.Text>
                            </Space>
                        </Card>
                    ) : posts?.length ? (
                        <Row gutter={[16, 16]}>
                            {posts.map((p, i) => (
                                <Col xs={24} key={p?.id || i}>
                                    {/* Render as PlanCard in moderation mode; keep your existing PlanCard logic */}
                                    <PlanCard
                                        item={{ ...(p?.ContentPlan || p), id: (p?.ContentPlan?.id || p?.id), uniqueIndexId: p?.id }}
                                        index={i}
                                        isModeration = {true}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <Card className="shadow-sm" bodyStyle={{ padding: 32, textAlign: "center" }}>
                            <Space direction="vertical">
                                <Typography.Title level={5} style={{ margin: 0 }}>
                                    {filter === "user_approved"
                                        ? t("moderation.emptyModerationListTitle") || t("functions.nothingFound")
                                        : t("moderation.emptyArchiveTitle") || t("functions.nothingFound")}
                                </Typography.Title>
                                <Typography.Text type="secondary">
                                    {filter === "user_approved"
                                        ? t("moderation.emptyModerationListHint") || ""
                                        : t("moderation.emptyArchiveHint") || ""}
                                </Typography.Text>
                            </Space>
                        </Card>
                    )}
                </div>

                {/* Pagination */}
                {(posts?.length > 0 || pagination.page > 1) && (
                    <Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
                        <Row justify="center">
                            <Pagination
                                size={window.innerWidth < 520 ? "small" : "default"}
                                hideOnSinglePage
                                disabled={isPostsLoading}
                                onChange={(page, pageSize) => {
                                    localStorage.setItem("paginationSize", pageSize);
                                    mergeStateObject(setPagination, { page });
                                    setPagesByFilter((prev) => ({ ...prev, [filter]: page }));
                                    setIsPostsLoading(true);
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                current={pagination.page}
                                pageSize={pagination.size}
                                total={pagination.count}
                                showSizeChanger={false}
                            />
                        </Row>
                    </Card>
                )}
            </Layout.Content>

            {/* Mobile app nav spacer + component */}
            <div style={{ height: 70 }} />
            <AppNavigation />
        </Layout>
    );

};

export default ModerationPage;
